import { Eye, EyeOff, Loader2, Shield, Trophy } from 'lucide-react'
import { useQueryClient } from '@tanstack/react-query'
import { useUpdateCustomerProfile } from '@/lib/queries/dashboard'
import { dashboardKeys, leaderboardKeys } from '@/lib/queries/keys'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'
import { getTier } from '@/lib/xp/tiers'

export interface StudentRanking {
  rankingPosition: number
  totalStudents: number
  rankingOptOut: boolean
  totalXp: number
  currentLevel: number
}

/**
 * A própria posição, SEMPRE — inclusive fora do top e inclusive para quem pediu
 * para sair da lista.
 *
 * Os dois casos vêm do `quick-stats`, não da lista: a rota do ranking devolve
 * só o topo, e quem optou por sair não está nela nem quando é o primeiro
 * colocado. Se este card lesse a lista, o aluno em 40º e o aluno oculto veriam
 * "sem posição" — o opt-out viraria punição, que é justamente o que a decisão
 * D4 recusou.
 */
export function LeaderboardMe({ ranking }: { ranking: StudentRanking }) {
  const tier = getTier(ranking.currentLevel)
  const queryClient = useQueryClient()
  const updateProfileMutation = useUpdateCustomerProfile()

  /**
   * 🔴 O controle que faltava.
   *
   * A coluna, as duas queries e o painel de "oculto" nasceram juntos — e nenhum
   * caminho de escrita. O aluno lia que o nome dele aparece para os colegas e
   * não tinha como desligar; o painel abaixo era um estado que ninguém
   * conseguia alcançar. Opt-out só com leitura é a metade que expõe.
   */
  function toggleLeaderboardVisibility() {
    updateProfileMutation.mutate(
      { leaderboardOptOut: !ranking.rankingOptOut },
      {
        onSuccess: () => {
          // As duas: o card lê do quick-stats, a lista some/volta no ranking.
          queryClient.invalidateQueries({ queryKey: dashboardKeys.quickStats() })
          queryClient.invalidateQueries({ queryKey: leaderboardKeys.all() })
        },
      },
    )
  }
  // 0 = o aluno não está na população (customer inativo ou excluído). Não é
  // "último lugar": é ausência de posição, e mostrar "#0" seria mentira.
  const isUnranked = ranking.rankingPosition <= 0

  return (
    <div className="rounded-xl border border-border bg-card p-5">
      <div className="flex flex-wrap items-center gap-4">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-primary/10">
          {isUnranked ? (
            <Trophy className="h-6 w-6 text-muted-foreground" />
          ) : (
            <span className="text-xl font-bold tabular-nums text-primary">
              #{ranking.rankingPosition}
            </span>
          )}
        </div>

        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-foreground">
            {isUnranked
              ? 'Você ainda não está no ranking'
              : `Sua posição — #${ranking.rankingPosition} de ${ranking.totalStudents}`}
          </p>
          <p className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Shield className={cn('h-3 w-3', tier.color)} />
              Nível {ranking.currentLevel} · {tier.name}
            </span>
            <span>·</span>
            <span>{ranking.totalXp.toLocaleString('pt-BR')} XP</span>
          </p>
        </div>
      </div>

      <div className="mt-4 flex items-start gap-3 rounded-lg border border-border bg-muted/40 p-3">
        {ranking.rankingOptOut ? (
          <EyeOff className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        ) : (
          <Eye className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
        )}
        <div className="flex-1 space-y-1 text-xs">
          <p className="font-medium text-foreground">
            {ranking.rankingOptOut
              ? 'Você está oculto para os colegas'
              : 'Seu nome aparece na lista pública'}
          </p>
          <p className="text-muted-foreground">
            {ranking.rankingOptOut
              ? 'Seu nome não aparece na lista pública. Sua posição continua valendo e continua sendo exibida aqui para você — sair da lista esconde, não pune.'
              : 'Os outros alunos deste espaço veem seu nome, nível e XP no ranking.'}
          </p>
        </div>
        <button
          type="button"
          onClick={toggleLeaderboardVisibility}
          disabled={updateProfileMutation.isPending}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-border bg-background px-3 py-1.5 text-[11px] font-medium text-foreground transition-colors hover:bg-muted disabled:opacity-50"
        >
          {updateProfileMutation.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
          {ranking.rankingOptOut ? 'Voltar para a lista' : 'Sair da lista'}
        </button>
      </div>
    </div>
  )
}

export function LeaderboardMeSkeleton() {
  return (
    <div className="flex items-center gap-4 rounded-xl border border-border bg-card p-5">
      <Skeleton className="h-14 w-14 rounded-xl" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-52" />
        <Skeleton className="h-3 w-36" />
      </div>
    </div>
  )
}
